import React from "react";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Building2,
  Stethoscope,
  SmilePlus,
  HandCoins,
} from "lucide-react";

interface HealthPlanningFormProps {
  formValues: Record<string, any>;
  onFieldChange: (fieldId: string, value: any) => void;
  className?: string;
}

// Insurance providers (Krankenkassen)
const HEALTH_INSURANCE_PROVIDERS = [
  { id: "oegk", label: "ÖGK" },
  { id: "svs_sach", label: "SVS-Sach" },
  { id: "svs_geld", label: "SVS-Geld" },
  { id: "betriebs_kk", label: "Betriebs-KK" },
  { id: "bvaeb", label: "BVAEB" },
  { id: "sonstige", label: "Sonstige", hasInput: true },
];

// Important health areas with icons
const HEALTH_AREAS = [
  { id: "privatklinik", label: "Privatklinik", icon: Building2 },
  { id: "privatarzt", label: "Privatarzt", icon: Stethoscope },
  { id: "zahnarzt", label: "Zahnarzt", icon: SmilePlus },
  { id: "geldleistung", label: "Geldleistung", icon: HandCoins },
];

// Additional checkbox options
const ADDITIONAL_OPTIONS = [
  { id: "zwei_einbettzimmer", label: "Zwei- oder Einbettzimmer" },
  { id: "alternativmedizin", label: "Alternativmedizin" },
  { id: "europadeckung", label: "Europadeckung" },
  { id: "weltweite_deckung", label: "Weltweite Deckung" },
  { id: "fitness_vorsorge", label: "Fitness- und Vorsorge" },
  { id: "leistbarkeit_pension", label: "Leistbarkeit in der Pension" },
  { id: "digitale_services", label: "Digitale Services" },
  { id: "uebernahme_begleitkosten", label: "Übernahme der Begleitkosten" },
  { id: "reiseversicherung", label: "Reiseversicherung" },
  { id: "fachaerzte_akute_versorgung", label: "Fachärzte für akute Versorgung" },
];

export function HealthPlanningForm({
  formValues,
  onFieldChange,
  className,
}: HealthPlanningFormProps) {
  const selectedProviders: Record<string, boolean> = formValues.gesundheitskasse || {};
  const sonstigeText: string = formValues.gesundheitskasse_sonstige || "";
  const selectedAreas: Record<string, boolean> = formValues.gesundheitsbereiche || {};
  const selectedOptions: Record<string, boolean> = formValues.gesundheitsoptionen || {};
  const notes: string = formValues.gesundheit_notizen || "";

  const toggleProvider = (providerId: string) => {
    const updated = { ...selectedProviders, [providerId]: !selectedProviders[providerId] };
    onFieldChange("gesundheitskasse", updated);
  };

  const toggleArea = (areaId: string) => {
    const updated = { ...selectedAreas, [areaId]: !selectedAreas[areaId] };
    onFieldChange("gesundheitsbereiche", updated);
  };

  const toggleOption = (optionId: string) => {
    const updated = { ...selectedOptions, [optionId]: !selectedOptions[optionId] };
    onFieldChange("gesundheitsoptionen", updated);
  };

  return (
    <div className={cn("p-6 space-y-8", className)}>
      {/* Title */}
      <div className="text-left">
        <h2 className="text-2xl md:text-3xl font-bold">
          <span className="text-foreground">Gesundheit </span>
          <span className="text-primary">erhalten</span>
        </h2>
        <p className="text-lg italic text-muted-foreground mt-1">
          Beste medizinische Versorgung.
        </p>
      </div>

      {/* Section: Gesetzliche Krankenversicherung */}
      <div className="space-y-4">
        <h3 className="font-semibold text-foreground">
          Bei welcher Gesundheitskasse sind Sie gesetzlich versichert?
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
          {HEALTH_INSURANCE_PROVIDERS.map((provider) => (
            <div key={provider.id} className="flex items-center gap-2">
              <Checkbox
                id={`provider-${provider.id}`}
                checked={selectedProviders[provider.id] || false}
                onCheckedChange={() => toggleProvider(provider.id)}
              />
              <Label
                htmlFor={`provider-${provider.id}`}
                className="text-sm cursor-pointer flex items-center gap-2"
              >
                {provider.label}
                {provider.hasInput && (
                  <Input
                    value={sonstigeText}
                    onChange={(e) => onFieldChange("gesundheitskasse_sonstige", e.target.value)}
                    placeholder="_______________"
                    className="h-7 w-28 text-sm border-b border-t-0 border-x-0 rounded-none px-1"
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* Section: Wichtige Bereiche - Tiles with Checkboxes */}
      <div className="space-y-4">
        <h3 className="font-semibold text-foreground">
          Welche Bereiche sind Ihnen besonders wichtig?
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {HEALTH_AREAS.map((area) => {
            const Icon = area.icon;
            const isSelected = selectedAreas[area.id];
            return (
              <button
                key={area.id}
                type="button"
                onClick={() => toggleArea(area.id)}
                className={cn(
                  "relative flex flex-col items-start p-4 rounded-lg border-2 transition-all text-left min-h-[100px]",
                  isSelected
                    ? "bg-primary/10 border-primary"
                    : "bg-muted/30 border-transparent hover:border-primary/50"
                )}
              >
                <span className="text-sm font-semibold text-primary leading-snug pr-8">
                  {area.label}
                </span>
                <Icon className={cn(
                  "h-10 w-10 mt-auto",
                  isSelected ? "text-primary" : "text-muted-foreground"
                )} />
                <div className="absolute top-3 right-3">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleArea(area.id)}
                    className="pointer-events-none"
                  />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Section: Additional Options - Two Columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
        {ADDITIONAL_OPTIONS.map((option) => (
          <div key={option.id} className="flex items-center gap-3">
            <Checkbox
              id={`option-${option.id}`}
              checked={selectedOptions[option.id] || false}
              onCheckedChange={() => toggleOption(option.id)}
            />
            <Label
              htmlFor={`option-${option.id}`}
              className="text-sm cursor-pointer"
            >
              {option.label}
            </Label>
          </div>
        ))}
      </div>

      {/* Section: Notizen */}
      <div className="space-y-3">
        <div className="border-2 border-muted rounded-lg p-4">
          <Label className="text-sm font-semibold text-foreground">Notizen:</Label>
          <Textarea
            value={notes}
            onChange={(e) => onFieldChange("gesundheit_notizen", e.target.value)}
            placeholder="Ihre Notizen hier..."
            className="mt-2 min-h-[120px] resize-none"
          />
        </div>
      </div>
    </div>
  );
}
